import {DocumentNode, gql} from '@apollo/client';
import React from 'react';

import DataConnector from 'connect/DataConnector';
import {LocationSchema, StorableSchema} from 'samples/storables/StorableSchemaConstants';
import {SchemaOwnerQuery} from 'schemas/SchemaOwnerUtil';

import {LocationSchemaSelectorOwnerQuery} from './__generated/LocationSchemaSelectorOwnerQuery';
import * as SampleSchemaActions from './SampleSchemaActions';
import SampleSchemaStore from './SampleSchemaStore';
import StorableSchemaSelector from './StorableSchemaSelector';

export interface LocationSchemaSelectorProps {
  error?: string;
  // Called with `(schema | null)` if `shouldShowNoSchemaOption` is true.  Otherwise, called with `(schema)`.
  onChange: (schema: StorableSchema | null) => void;
  registryId?: string;
  selectedLocationSchemaId: string | null;
  // Marked as optional here but there is a default prop for it so we can rely on it being present in the component.
  shouldShowNoSchemaOption?: boolean;
  locationSchemas?: Array<{
    id: string;
    name: string;
  }>;
}

interface EnhancedProps {
  locationSchemas: Array<LocationSchema> | null;
}

class LocationSchemaSelectorConnector extends DataConnector<LocationSchemaSelectorProps, EnhancedProps> {
  shouldSubscribeToRedux = false;
  stores = [SampleSchemaStore];

  enhanceProps(): EnhancedProps {
    return {locationSchemas: this.sampleSchemaStore().getLocationSchemas(this.props.registryId)};
  }
}

export const LOCATION_SCHEMA_SELECTOR_OWNER_QUERY: DocumentNode = gql`
  query LocationSchemaSelectorOwnerQuery @skipAudit @engComponent(name: INVENTORY) {
    locationSchemas {
      id
      registry {
        id
        organization {
          id
          name
        }
      }
    }
  }
`;

class LocationSchemaSelector extends React.Component<LocationSchemaSelectorProps & EnhancedProps> {
  static defaultProps = {
    shouldShowNoSchemaOption: false,
  };

  componentDidMount(): void {
    SampleSchemaActions.fetchAllLocationSchemas();
  }

  render(): JSX.Element {
    return (
      <SchemaOwnerQuery<LocationSchemaSelectorOwnerQuery>
        query={LOCATION_SCHEMA_SELECTOR_OWNER_QUERY}
        getIdOwnerTuples={(data) =>
          data.locationSchemas.map((locationSchema) => ({
            id: locationSchema.id,
            owner: locationSchema.registry.organization.name,
          }))
        }
      >
        {({getSchemaOwner}) => (
          <StorableSchemaSelector
            error={this.props.error}
            onChange={this.props.onChange}
            schemas={this.props.locationSchemas}
            selectedSchemaId={this.props.selectedLocationSchemaId}
            shouldShowNoSchemaOption={this.props.shouldShowNoSchemaOption}
            getSchemaOwner={getSchemaOwner}
            // Overridden by props from redux
            starredSchemaIds={[]}
          />
        )}
      </SchemaOwnerQuery>
    );
  }
}

export default LocationSchemaSelectorConnector.connect(LocationSchemaSelector);
